/**
 * Request/response schemas for the aesthetics/med-spa specialty module (`BACKLOG.md` M9). Same
 * reasoning as `dermatologyRequests.ts`: these live in `shared/` so the server validates against the
 * exact schema the web app infers its types from, every object schema is strict so an unrecognised
 * key is a rejection rather than silently ignored, and no server-owned value (an id, `orgId`, a
 * timestamp, `signedByUserId`, a computed total) appears in any request schema — the route layer
 * supplies those from the session or derives them, never taking them from the body.
 */

import { z } from 'zod';

import { BODY_MAP_REGIONS, BODY_MAP_SIDES } from './bodyMapping.ts';
import {
  calendarDateSchema,
  confidentialityReasonsInputSchema,
  MAX_LOT_NUMBER_LENGTH,
} from './clinicalRequests.ts';
import {
  BILLING_CADENCES,
  CASH_PAY_PRODUCT_TYPES,
  CASH_PAY_PURCHASE_STATUSES,
  PAYMENT_METHODS,
  tracksUnitBalance,
} from './aesthetics/cashPay.ts';
import { CONSENT_DOCUMENT_TYPES, CONSENT_SIGNING_METHODS } from './consentDocuments.ts';
import { CLINICAL_ERROR_CODES } from './encounterRequests.ts';
import { INJECTABLE_PRODUCT_CLASSES } from './aesthetics/injectables.ts';
import { PHOTO_ANGLES } from './photoDocumentation.ts';
import { RETAIL_PAYMENT_METHODS } from './aesthetics/retailPos.ts';

export const MAX_AESTHETICS_NAME_LENGTH = 200;
export const MAX_VERSION_LENGTH = 100;
export const MAX_STORAGE_REF_LENGTH = 1000;
export const MAX_REASON_LENGTH = 500;
export const MAX_SKU_LENGTH = 100;
export const MAX_PROVIDER_NAME_LENGTH = 100;
export const MAX_LABEL_LENGTH = 200;

const trimmed = (max: number) => z.string().trim().min(1).max(max);
const optionalTrimmed = (max: number) => trimmed(max).optional();

/** A non-negative decimal string with at most two fraction digits — every quantity in this module
 * (injectable units, a cash-pay unit balance) is represented this way end to end, matching
 * `aesthetics/cashPay.ts`'s own decimal-string-in/decimal-string-out reasoning: never a JS `number`,
 * so no float ever touches a quantity a redemption or a recall depends on being exact. */
const decimalString = z
  .string()
  .trim()
  .regex(/^\d+(\.\d{1,2})?$/, 'must be a non-negative number with at most two decimal places');

const bodyMapRegionSchema = z.enum(BODY_MAP_REGIONS);
const bodyMapSideSchema = z.enum(BODY_MAP_SIDES);

// --- Consent documents ----------------------------------------------------------------------------

export const createConsentDocumentRequestSchema = z
  .object({
    consentType: z.enum(CONSENT_DOCUMENT_TYPES),
    documentVersion: optionalTrimmed(MAX_VERSION_LENGTH),
    signedOn: calendarDateSchema,
    method: z.enum(CONSENT_SIGNING_METHODS).default('in_person'),
    confidentialityReasons: confidentialityReasonsInputSchema,
  })
  .strict();
export type CreateConsentDocumentRequest = z.infer<typeof createConsentDocumentRequestSchema>;

export const consentDocumentResponseSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  // Response, not request — plain strings, matching `dermatologyRequests.ts`'s own split: the
  // narrow request-side enums guard what a caller may write; a value already stored is read back
  // as-is.
  consentType: z.string(),
  documentVersion: z.string().nullable(),
  signedOn: calendarDateSchema,
  signedByUserId: z.uuid(),
  method: z.string(),
  // Plain string, not `z.iso.datetime()` — a DB readback of an `instant()` column, and Postgres's
  // own timestamptz text format is not strict ISO 8601 (a space separator, "+00" not "+00:00").
  // The identical bug just found and fixed in `dermatologyRequests.ts`'s own
  // `fitzpatrickRecordedAt`/`ipledgeRecordedAt` (2026-08-18) — same shape, same file's own
  // "response, not request" comment above already argued the right rule, the schema just didn't
  // follow it for this one field.
  revokedAt: z.string().nullable(),
  confidentialityReasons: z.array(z.string()),
});
export type ConsentDocumentResponse = z.infer<typeof consentDocumentResponseSchema>;

export const consentDocumentEnvelopeSchema = z.object({ consent: consentDocumentResponseSchema });
export const consentDocumentListEnvelopeSchema = z.object({
  consents: z.array(consentDocumentResponseSchema),
});

// --- Photo documentation --------------------------------------------------------------------------

export const createPatientPhotoRequestSchema = z
  .object({
    angle: z.enum(PHOTO_ANGLES),
    takenAt: z.iso.datetime({ offset: true }),
    storageRef: trimmed(MAX_STORAGE_REF_LENGTH),
    lightingStandardized: z.boolean().default(false),
    /** The `photograph`-type consent document on file authorizing this photo — validated
     * server-side to actually be that type, for this patient, and not revoked (`photoService.ts`'s
     * `requirePhotographConsent`), never merely trusted because an id was supplied. */
    photographConsentId: z.uuid(),
    confidentialityReasons: confidentialityReasonsInputSchema,
  })
  .strict();
export type CreatePatientPhotoRequest = z.infer<typeof createPatientPhotoRequestSchema>;

export const patientPhotoResponseSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  angle: z.string(),
  // Plain string, not `z.iso.datetime()` — same reasoning as `revokedAt` above: this is a DB
  // readback of an `instant()` column, not the client-submitted request value it's named after.
  takenAt: z.string(),
  storageRef: z.string(),
  lightingStandardized: z.boolean(),
  photographConsentId: z.uuid(),
  confidentialityReasons: z.array(z.string()),
});
export type PatientPhotoResponse = z.infer<typeof patientPhotoResponseSchema>;

export const patientPhotoEnvelopeSchema = z.object({ photo: patientPhotoResponseSchema });
export const patientPhotoListEnvelopeSchema = z.object({
  photos: z.array(patientPhotoResponseSchema),
});

// --- Injectable charting ---------------------------------------------------------------------------

/**
 * Lot number is "effectively mandatory" (`BACKLOG.md` M9), enforced here rather than only at the
 * database `CHECK`: at least one of `lotNumber`/`lotNumberUnavailableReason` must be present, so a
 * malformed submission is rejected as `invalid_request` with a real Zod issue rather than reaching
 * Postgres and surfacing as an opaque constraint-violation 500. `aesthetics/injectables.ts`'s
 * `hasLotNumberOnFile` states the identical rule as a pure function for anywhere else that needs to
 * ask the same question without re-parsing a request.
 */
export const createInjectableAdministrationRequestSchema = z
  .object({
    productName: trimmed(MAX_AESTHETICS_NAME_LENGTH),
    productClass: z.enum(INJECTABLE_PRODUCT_CLASSES),
    lotNumber: optionalTrimmed(MAX_LOT_NUMBER_LENGTH),
    lotNumberUnavailableReason: optionalTrimmed(MAX_REASON_LENGTH),
    units: decimalString,
    bodyMapRegion: bodyMapRegionSchema,
    bodyMapSide: bodyMapSideSchema.optional(),
    bodyMapLabel: optionalTrimmed(MAX_LABEL_LENGTH),
    administeredOn: calendarDateSchema,
    /** The consent document on file for this `productClass` — validated server-side
     * (`injectableService.ts`) to match `requiredConsentTypeForProductClass(productClass)` (or
     * `treatment_general` for `other`), belong to this patient, and not be revoked. */
    consentDocumentId: z.uuid(),
    confidentialityReasons: confidentialityReasonsInputSchema,
  })
  .strict()
  .refine(
    (value) => value.lotNumber !== undefined || value.lotNumberUnavailableReason !== undefined,
    {
      message: 'lotNumber or lotNumberUnavailableReason is required',
      path: ['lotNumber'],
    },
  );
export type CreateInjectableAdministrationRequest = z.infer<
  typeof createInjectableAdministrationRequestSchema
>;

export const injectableAdministrationResponseSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  productName: z.string(),
  productClass: z.string(),
  lotNumber: z.string().nullable(),
  lotNumberUnavailableReason: z.string().nullable(),
  units: z.string(),
  bodyMapRegion: z.string(),
  bodyMapSide: z.string().nullable(),
  bodyMapLabel: z.string().nullable(),
  administeredOn: calendarDateSchema,
  administeredByUserId: z.uuid(),
  consentDocumentId: z.uuid(),
  confidentialityReasons: z.array(z.string()),
});
export type InjectableAdministrationResponse = z.infer<
  typeof injectableAdministrationResponseSchema
>;

export const injectableAdministrationEnvelopeSchema = z.object({
  administration: injectableAdministrationResponseSchema,
});
export const injectableAdministrationListEnvelopeSchema = z.object({
  administrations: z.array(injectableAdministrationResponseSchema),
});

// --- Cash-pay billing: product catalog -------------------------------------------------------------

/**
 * `unitsIncluded` may only be given for a product type `tracksUnitBalance` says carries a redeemable
 * balance (`package`/`unit_bank`); `billingCadence` only for `membership`. Enforced by `.refine`
 * rather than by a discriminated union keyed on `productType`, matching this module's own preference
 * elsewhere (`updateDermatologyProfileRequestSchema`'s at-least-one-field refine) for a single flat
 * shape with a stated cross-field rule over a more type-safe but harder-to-extend union.
 */
export const createCashPayProductRequestSchema = z
  .object({
    name: trimmed(MAX_AESTHETICS_NAME_LENGTH),
    productType: z.enum(CASH_PAY_PRODUCT_TYPES),
    priceCents: z.number().int().nonnegative(),
    unitsIncluded: decimalString.optional(),
    billingCadence: z.enum(BILLING_CADENCES).optional(),
  })
  .strict()
  .refine((value) => value.unitsIncluded === undefined || tracksUnitBalance(value.productType), {
    message: 'unitsIncluded is only valid for a package or unit_bank product',
    path: ['unitsIncluded'],
  })
  .refine((value) => value.billingCadence === undefined || value.productType === 'membership', {
    message: 'billingCadence is only valid for a membership product',
    path: ['billingCadence'],
  });
export type CreateCashPayProductRequest = z.infer<typeof createCashPayProductRequestSchema>;

export const cashPayProductResponseSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  productType: z.string(),
  priceCents: z.number().int(),
  unitsIncluded: z.string().nullable(),
  billingCadence: z.string().nullable(),
  active: z.boolean(),
});
export type CashPayProductResponse = z.infer<typeof cashPayProductResponseSchema>;

export const cashPayProductEnvelopeSchema = z.object({ product: cashPayProductResponseSchema });
export const cashPayProductListEnvelopeSchema = z.object({
  products: z.array(cashPayProductResponseSchema),
});

// --- Cash-pay billing: patient purchases -----------------------------------------------------------

/**
 * `priceCentsPaid`, `unitsPurchased`, `productType`, and `productName` are never accepted here —
 * `server/src/aesthetics/cashPayService.ts` snapshots all four from the catalog product row at
 * purchase time. A price is exactly the kind of value `claude-kit/rules/invariants.md`'s
 * "server-authoritative values never come from the request" exists for: this system, not the
 * caller, owns what a product costs.
 */
export const createCashPayPurchaseRequestSchema = z
  .object({
    productId: z.uuid(),
    paymentMethod: z.enum(PAYMENT_METHODS),
    bnplProvider: optionalTrimmed(MAX_PROVIDER_NAME_LENGTH),
    purchasedOn: calendarDateSchema,
  })
  .strict()
  .refine((value) => value.bnplProvider === undefined || value.paymentMethod === 'bnpl', {
    message: 'bnplProvider is only valid when paymentMethod is bnpl',
    path: ['bnplProvider'],
  });
export type CreateCashPayPurchaseRequest = z.infer<typeof createCashPayPurchaseRequestSchema>;

export const redeemCashPayUnitsRequestSchema = z.object({ units: decimalString }).strict();
export type RedeemCashPayUnitsRequest = z.infer<typeof redeemCashPayUnitsRequestSchema>;

export const cashPayPurchaseResponseSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  productId: z.uuid(),
  productType: z.string(),
  productName: z.string(),
  unitsPurchased: z.string().nullable(),
  unitsRemaining: z.string().nullable(),
  priceCentsPaid: z.number().int(),
  paymentMethod: z.string(),
  bnplProvider: z.string().nullable(),
  purchasedOn: calendarDateSchema,
  status: z.enum(CASH_PAY_PURCHASE_STATUSES),
});
export type CashPayPurchaseResponse = z.infer<typeof cashPayPurchaseResponseSchema>;

export const cashPayPurchaseEnvelopeSchema = z.object({
  purchase: cashPayPurchaseResponseSchema,
});
export const cashPayPurchaseListEnvelopeSchema = z.object({
  purchases: z.array(cashPayPurchaseResponseSchema),
});

// --- Retail POS -------------------------------------------------------------------------------------

/** No `totalPriceCents` field — see `aesthetics/retailPos.ts`'s `computeRetailSaleTotalCents` header
 * for why the total is always server-derived rather than client-supplied. */
export const createRetailSaleRequestSchema = z
  .object({
    productName: trimmed(MAX_AESTHETICS_NAME_LENGTH),
    sku: optionalTrimmed(MAX_SKU_LENGTH),
    quantity: z.number().int().positive(),
    unitPriceCents: z.number().int().nonnegative(),
    paymentMethod: z.enum(RETAIL_PAYMENT_METHODS),
    soldOn: calendarDateSchema,
  })
  .strict();
export type CreateRetailSaleRequest = z.infer<typeof createRetailSaleRequestSchema>;

export const retailSaleResponseSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  productName: z.string(),
  sku: z.string().nullable(),
  quantity: z.number().int(),
  unitPriceCents: z.number().int(),
  totalPriceCents: z.number().int(),
  paymentMethod: z.string(),
  soldOn: calendarDateSchema,
  soldByUserId: z.uuid(),
});
export type RetailSaleResponse = z.infer<typeof retailSaleResponseSchema>;

export const retailSaleEnvelopeSchema = z.object({ sale: retailSaleResponseSchema });
export const retailSaleListEnvelopeSchema = z.object({ sales: z.array(retailSaleResponseSchema) });

// --- Failure vocabulary -----------------------------------------------------------------------

/**
 * Closed union, extending `encounterRequests.ts`'s `CLINICAL_ERROR_CODES` the same way
 * `dermatologyRequests.ts`'s `DERMATOLOGY_ERROR_CODES` does. `not_entitled` mirrors that module's
 * reasoning exactly; `consent_not_on_file` and `insufficient_balance` are the two codes genuinely
 * new here — a photo or injectable write refused for lacking the right consent, and a unit
 * redemption refused for exceeding the remaining balance (`aesthetics/cashPay.ts`'s
 * `redeemUnits`), both distinct from a generic `invalid_request` because each names a specific,
 * actionable reason the web app can branch on rather than show generic prose for.
 */
export const AESTHETICS_ERROR_CODES = [
  ...CLINICAL_ERROR_CODES,
  'not_entitled',
  'consent_not_on_file',
  'insufficient_balance',
] as const;
export type AestheticsErrorCode = (typeof AESTHETICS_ERROR_CODES)[number];
